import { DateTime } from 'luxon';

function getDayType(dayOfWeek) {
  return dayOfWeek === 0 || dayOfWeek === 6 ? 'weekend' : 'weekday';
}

function normalizeHour(hour) {
  let res = hour;

  while (res < 0) res += 24;
  while (res >= 24) res -= 24;

  return res;
}

function getTimezoneOffset(date, timezone) {
  try {
    if (!timezone) return 0;
    const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
    const tzDate = new Date(date.toLocaleString('en-US', { timeZone: timezone }));
    const offsetMs = tzDate.getTime() - utcDate.getTime();

    return offsetMs / (1000 * 60 * 60);
  } catch (error) {
    console.warn(`Error getting offset for ${timezone}:`, error);

    return 0;
  }
}

// Fixed function to properly check if a time slot overlaps with teacher availability
function isSlotWithinTeacherShift(studentStartIST, studentEndIST, teacherShift) {
  const { start: teacherStart, end: teacherEnd } = teacherShift;

  // Handle normal shift (doesn't cross midnight)
  if (teacherStart <= teacherEnd) {
    return studentStartIST >= teacherStart && studentEndIST <= teacherEnd;
  }

  // Handle night shift (crosses midnight)
  // Teacher is available from start time to 24:00 AND from 00:00 to end time
  const isInEveningPortion = studentStartIST >= teacherStart && studentStartIST < 24;
  const isInMorningPortion = studentEndIST > 0 && studentEndIST <= teacherEnd;

  // For night shifts, the slot must be entirely within one portion
  // (either entirely in evening portion OR entirely in morning portion)
  if (isInEveningPortion) {
    return studentEndIST <= 24; // Must end before midnight
  }

  if (isInMorningPortion) {
    return studentStartIST >= 0; // Must start after midnight
  }

  return false;
}

function getTimezoneAbbreviation(date, timezone) {
  try {
    if (!timezone) return 'UTC';
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    }).formatToParts(date);

    return (
      parts.find(part => part.type === 'timeZoneName')?.value || timezone.split('/').pop()
    );
  } catch (error) {
    return (
      timezone
        .split('/')
        .pop()
        .replace('_', ' ') || 'UTC'
    );
  }
}

function getLocalTimezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

// function formatTimeLabel(startHour, endHour) {
//   const formatHour = hour => {
//     if (hour > 12) return hour - 12;
//     if (hour === 0) return 12;

//     return hour;
//   };

//   const periodStart = startHour >= 12 ? 'PM' : 'AM';
//   const periodEnd = endHour >= 12 ? 'PM' : 'AM';
//   const displayStart = formatHour(startHour);
//   const displayEnd = formatHour(endHour);

//   return `${displayStart} ${periodStart} - ${displayEnd} ${periodEnd}`;
// }

function formatTimeLabel(startHour, endHour) {
  const formatHour = hour => {
    const wholeHour = Math.floor(hour);
    const minutes = Math.round((hour % 1) * 60);

    // Convert to 12-hour format
    let displayHour = wholeHour;
    if (displayHour > 12) displayHour = displayHour - 12;
    if (displayHour === 0) displayHour = 12;

    // Format minutes
    const minuteStr = minutes === 0 ? '00' : minutes.toString().padStart(2, '0');

    return `${displayHour}:${minuteStr}`;
  };

  const formatPeriod = hour => {
    return Math.floor(hour) >= 12 ? 'PM' : 'AM';
  };

  const periodStart = formatPeriod(startHour);
  const periodEnd = formatPeriod(endHour);
  const displayStart = formatHour(startHour);
  const displayEnd = formatHour(endHour);

  // Only show period once if both times are in same period
  if (periodStart === periodEnd) {
    return `${displayStart} - ${displayEnd} ${periodEnd}`;
  } else {
    return `${displayStart} ${periodStart} - ${displayEnd} ${periodEnd}`;
  }
}

function getInitials(phrase) {
  if (typeof phrase !== 'string') return '';

  return phrase
    .trim()
    .split(/\s+/) // Split by one or more spaces
    .filter(Boolean) // Remove empty strings
    .map(word => word[0]) // Get first character
    .join('')
    .toUpperCase();
}

// function formatLocalTimeLabel(epochTime) {
//   const start = DateTime.fromSeconds(epochTime);
//   const end = start.plus({ hours: 0.5 });

//   const startHour = start.toFormat('h:mm');
//   const endHour = end.toFormat('h:mm');
//   const period = start.toFormat('a');
//   const tzAbbr = getInitials(start.offsetNameLong); // e.g., IST

//   return `${startHour} ${period} - ${endHour} ${period} ${tzAbbr}`;
// }

function formatLocalTimeLabel(epochTime) {
  const start = DateTime.fromSeconds(epochTime);
  const end = start.plus({ hours: 1 });

  const startHour = start.toFormat('h:mm');
  const endHour = end.toFormat('h:mm');
  const startPeriod = start.toFormat('a');
  const endPeriod = end.toFormat('a');
  const tzAbbr = getInitials(start.offsetNameLong); // e.g., IST

  // Only show period once if both times are in same period
  if (startPeriod === endPeriod) {
    return `${startHour} - ${endHour} ${startPeriod} ${tzAbbr}`;
  } else {
    return `${startHour} ${startPeriod} - ${endHour} ${endPeriod} ${tzAbbr}`;
  }
}

function createContinuousSlots(slots) {
  if (slots.length === 0) return slots;

  // Sort slots by start time
  slots.sort((a, b) => a.from - b.from);

  // Group slots by timezone to handle each timezone separately
  const slotsByTimezone = {};

  slots.forEach(slot => {
    const tz = slot.metadata.timezone;

    if (!slotsByTimezone[tz]) {
      slotsByTimezone[tz] = [];
    }
    slotsByTimezone[tz].push(slot);
  });

  const continuousSlots = [];

  // Process each timezone separately
  Object.keys(slotsByTimezone).forEach(timezone => {
    const tzSlots = slotsByTimezone[timezone];

    if (tzSlots.length === 0) return;

    // Start with the first slot
    // let currentSlot = tzSlots[0];

    // continuousSlots.push(currentSlot);
    continuousSlots.push(...tzSlots);

    // Create continuous chain
    // for (let i = 1; i < tzSlots.length; i++) {
    //   const nextSlot = tzSlots[i];

    //   // Check if this slot starts where the previous ends (continuous)
    //   if (nextSlot.from === currentSlot.to) {
    //     continuousSlots.push(nextSlot);
    //     currentSlot = nextSlot;
    //   } else if (nextSlot.from > currentSlot.to) {
    //     // There's a gap, but we can still add it as a new continuous chain
    //     continuousSlots.push(nextSlot);
    //     currentSlot = nextSlot;
    //   }
    //   // Skip if there's an overlap (nextSlot.from < currentSlot.to)
    // }
  });

  return continuousSlots.sort((a, b) => a.from - b.from);
}

const TEACHER_SHIFT_CONFIG = {
  // NAM region - 00:30-08:30 IST weekdays, 19:30-06:30[+1] IST weekends
  NAM: {
    weekdays: { start: 0.5, end: 8.5, days: [1, 2, 3, 4, 5] },
    weekends: { start: 19.5, end: 6.5, days: [0, 6] },
  },
  // EMEA region - 16:30-00:30[+1] IST weekdays, 11:30-22:30 IST weekends
  EMEA: {
    weekdays: { start: 16.5, end: 0.5, days: [1, 2, 3, 4, 5] },
    weekends: { start: 11.5, end: 22.5, days: [0, 6] },
  },
  // APAC region - 09:30-17:30 IST weekdays, 05:30-15:30 IST weekends
  APAC: {
    weekdays: { start: 9.5, end: 17.5, days: [1, 2, 3, 4, 5] },
    weekends: { start: 5.5, end: 15.5, days: [0, 6] },
  },
  NEZ: {
    weekdays: { start: 9.5, end: 17.5, days: [1, 2, 3, 4, 5] },
    weekends: { start: 5.5, end: 15.5, days: [0, 6] },
  },
  SGD: {
    weekdays: { start: 9.5, end: 17.5, days: [1, 2, 3, 4, 5] },
    weekends: { start: 5.5, end: 15.5, days: [0, 6] },
  },
  // ISC-x (IB, IG) region - 15:00-20:00 IST weekdays, 10:00-18:00 IST weekends
  IND: {
    weekdays: { start: 15, end: 20, days: [1, 2, 3, 4, 5] },
    weekends: { start: 10, end: 18, days: [0, 6] },
  },

  // Legacy regions (keeping for backward compatibility)
  // USA: { start: 19, end: 6.5, days: [0, 1, 2, 3, 4, 5, 6] },
  ROW: { start: 5.5, end: 23.5, days: [0, 1, 2, 3, 4, 5, 6] },
};

export function generateFormattedSchedule(options = {}) {
  // Ensure all nested objects have proper defaults
  const { region, bufferHours } = options;

  // console.log(`Generating schedule for region: ${region}, bufferHours: ${bufferHours}`);

  const defaultOptions = {
    teacherAvailability: {
      shifts: [], // Will be populated based on region and day type
      timezone: 'Asia/Kolkata', // Teacher Timezone
      blockedDates: [], // Ensure this is always an array
    },
    studentAvailability: {
      startHour: 10, // 10 AM Local Time
      endHour: 20, // 8 PM Local Time
      sessionDuration: 1,
      timezones: ['America/New_York'],
      availableDays: [0, 1, 2, 3, 4, 5, 6],
      blockedDates: [], // Added for consistency
    },
    lookaheadDays: 7,
    referenceDate: new Date(),
  };

  // Helper function to get teacher shift for a specific day
  const getTeacherShiftForDay = (regionName, dayOfWeek) => {
    const regionConfig = TEACHER_SHIFT_CONFIG[regionName || 'ROW'];

    // Handle new format with weekdays/weekends
    if (regionConfig && regionConfig.weekdays && regionConfig.weekends) {
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

      return isWeekend ? regionConfig.weekends : regionConfig.weekdays;
    }

    // Handle legacy format
    if (regionConfig && Array.isArray(regionConfig.days)) {
      return regionConfig;
    }

    // Fallback to ROW config
    return TEACHER_SHIFT_CONFIG.ROW;
  };

  // Helper function to get student time restrictions based on day type
  const getStudentTimeRestrictions = dayOfWeek => {
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

    if (isWeekend) {
      // Weekends: 10 am to 6 pm in local TZ
      return { startHour: 10, endHour: 18 };
    }

    // Weekdays: 3 pm to 8 pm in local TZ
    return { startHour: 15, endHour: 20 };
  };

  // Deep merge defaults with provided options
  const config = {
    teacherAvailability: {
      ...defaultOptions.teacherAvailability,
      ...(options.teacherAvailability || {}),
    },
    studentAvailability: {
      ...defaultOptions.studentAvailability,
      ...(options.studentAvailability || {}),
    },
    lookaheadDays: options.lookaheadDays || defaultOptions.lookaheadDays,
    referenceDate: options.referenceDate || defaultOptions.referenceDate,
  };

  // Ensure blockedDates is always an array
  if (!Array.isArray(config.teacherAvailability.blockedDates)) {
    config.teacherAvailability.blockedDates = [];
  }
  if (!Array.isArray(config.studentAvailability.blockedDates)) {
    config.studentAvailability.blockedDates = [];
  }

  const results = [];

  // Calculate the start date (72 hours from reference date)
  // Always use current time unless explicitly overridden
  // const now = new Date();
  const studentTimezone =
    config.studentAvailability?.timezones?.[0] || 'America/New_York';
  const now = DateTime.now().setZone(studentTimezone);
  const actualReferenceDate = now;
  // const actualReferenceDate = DateTime.fromJSDate(options.referenceDate || now, {
  // Add exactly 72 hours (72 * 60 * 60 * 1000 milliseconds)
  // const startDateTime = new Date(actualReferenceDate.getTime() + 72 * 60 * 60 * 1000);
  const startDateTime = actualReferenceDate.plus({ hours: bufferHours });

  // console.log('Reference Date:', actualReferenceDate.toISO());
  // console.log('Start DateTime:', startDateTime);

  // Debug logging (remove in production)
  // console.log('Current Time:', now.toISOString());
  // console.log('Current Time IST:', now.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }));
  // console.log('Reference Date:', actualReferenceDate.toISOString());
  // console.log('Start DateTime (72h later):', startDateTime.toISOString());
  // console.log(
  //   'Start DateTime IST:',
  //   startDateTime.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }),
  // );

  // Keep the full datetime (don't reset to midnight) for proper 72-hour delay
  // const startDate = new Date(startDateTime);

  // Generate slots for each day (starting from 72 hours after reference date)
  for (let i = 0; i < config.lookaheadDays; i++) {
    // Create date for this iteration
    // const currentDate = new Date(startDate);

    // currentDate.setDate(startDate.getDate() + i);

    // const dayOfWeek = currentDate.getDay();
    // const dateStr = currentDate.toISOString().split('T')[0];

    const currentDate = startDateTime.plus({ days: i });
    const dayOfWeek = currentDate.weekday % 7; // Luxon: 1=Monday...7=Sunday, JS: 0=Sunday...6=Saturday
    const dateStr = currentDate.toISODate();

    // Debug logging for each day being processed
    // console.log(
    //   `Day ${i}: ${dateStr} (${currentDate.toLocaleDateString('en-US', {
    //     weekday: 'long',
    //   })}) - IST: ${currentDate.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}`,
    // );

    // Skip if date is blocked by either party
    if (
      config.teacherAvailability.blockedDates.includes(dateStr) ||
      config.studentAvailability.blockedDates.includes(dateStr)
    ) {
      continue;
    }

    // Get teacher shift for this day using the region-based config
    const teacherShift = getTeacherShiftForDay(region, dayOfWeek);
    // console.log(`📅 Day ${dayOfWeek} (${currentDate.toFormat('cccc')}):`);
    // console.log('  Teacher Shift:', teacherShift);
    // console.log('  Teacher Shift Days:', teacherShift?.days);
    // console.log(
    //   '  Day included in teacher shift?',
    //   teacherShift?.days?.includes(dayOfWeek),
    // );
    // console.log('  Student available days:', config.studentAvailability.availableDays);
    // console.log(
    //   '  Day included in student availability?',
    //   config.studentAvailability.availableDays.includes(dayOfWeek),
    // );

    // Get student time restrictions for this day type
    const studentRestrictions = getStudentTimeRestrictions(dayOfWeek);

    // Skip if no matching teacher shift or day not available for the teacher
    if (
      !teacherShift ||
      !Array.isArray(teacherShift.days) ||
      !teacherShift.days.includes(dayOfWeek) ||
      !Array.isArray(config.studentAvailability.availableDays) ||
      !config.studentAvailability.availableDays.includes(dayOfWeek)
    ) {
      continue;
    }

    const daySlots = [];
    // const dayKey = Math.floor(currentDate.getTime() / 1000);
    const dayKey = Math.floor(currentDate.toSeconds());
    const dayLabel = currentDate.toLocaleString({
      // 🔧 NEW: Luxon formatting
      month: 'short',
      day: 'numeric',
    });
    // const dayLabel = currentDate.toLocaleDateString('en-US', {
    //   month: 'short',
    //   day: 'numeric',
    // });
    // const subLabel = currentDate.toLocaleDateString('en-US', { weekday: 'long' });
    const subLabel = currentDate.toLocaleString({ weekday: 'long' });
    const dayType = getDayType(dayOfWeek);

    // Helper function to determine time of day based on local start hour
    const getTimeOfDay = hour => {
      if (hour >= 10 && hour < 12) {
        return 'morning';
      }
      if (hour >= 12 && hour < 17) {
        return 'afternoon';
      }
      if (hour >= 17 && hour < 20) {
        return 'evening';
      }

      return 'other'; // fallback for any edge cases
    };

    // Helper to add formatted slot
    const addSlot = function addSlot(
      localStart,
      localEnd,
      istStart,
      istEnd,
      timezone,
      tzAbbr,
    ) {
      // const slotDate = new Date(currentDate);

      // slotDate.setHours(Math.floor(localStart), (localStart % 1) * 60, 0, 0);

      // const fromEpoch = Math.floor(slotDate.getTime() / 1000);
      const slotDateTime = currentDate.set({
        // 🔧 NEW: Luxon set() method
        hour: Math.floor(localStart),
        minute: (localStart % 1) * 60,
        second: 0,
        millisecond: 0,
      });
      const fromEpoch = Math.floor(slotDateTime.toSeconds());
      const toEpoch = fromEpoch + config.studentAvailability.sessionDuration * 3600;
      const slotKey = fromEpoch;

      daySlots.push({
        key: slotKey,
        from: fromEpoch,
        to: toEpoch,
        label: `${formatTimeLabel(localStart, localEnd)} ${tzAbbr}`,
        subLabel: formatLocalTimeLabel(fromEpoch, timezone),
        timeOfDay: getTimeOfDay(localStart),
        metadata: {
          timezone,
          abbreviation: tzAbbr,
          istTime: `${Math.floor(istStart)}:${Math.round((istStart % 1) * 60)
            .toString()
            .padStart(2, '0')}-${Math.floor(istEnd)}:${Math.round((istEnd % 1) * 60)
            .toString()
            .padStart(2, '0')}`,
        },
      });
    };

    // Check each student timezone
    config.studentAvailability.timezones.forEach(timezone => {
      const tzAbbr = getTimezoneAbbreviation(currentDate.toJSDate(), timezone);
      const tzOffset = getTimezoneOffset(currentDate.toJSDate(), timezone);
      const istOffset = 5.5; // IST is UTC+5:30

      // Generate slots using intersection logic:
      // Weekdays: teacher shift ∩ [3pm-8pm local]
      // Weekends: teacher shift ∩ [10am-6pm local]
      const localStartHour = studentRestrictions.startHour;
      const localEndHour = studentRestrictions.endHour;

      for (
        let hour = localStartHour;
        hour <= localEndHour - config.studentAvailability.sessionDuration;
        hour += 1
      ) {
        const endHour = hour + config.studentAvailability.sessionDuration;

        // Create the actual slot datetime
        // const slotDateTime = new Date(currentDate);

        // slotDateTime.setHours(Math.floor(hour), (hour % 1) * 60, 0, 0);
        const slotDateTime = currentDate.set({
          // currentDate is Luxon DateTime
          // hour: Math.floor(hour),
          hour,
          minute: (hour % 1) * 60,
          second: 0,
          millisecond: 0,
        });

        // Only include slots that are actually after the 72-hour mark
        // if (slotDateTime.getTime() < startDateTime.getTime()) {
        //   continue; // Skip this slot as it's before the 72-hour cutoff
        // }
        if (slotDateTime < startDateTime) {
          continue; // Skip this slot as it's before the 72-hour cutoff
        }

        // Convert student local time to IST
        const utcHour = hour - tzOffset;
        const istStartHour = normalizeHour(utcHour + istOffset);
        const istEndHour = normalizeHour(
          istStartHour + config.studentAvailability.sessionDuration,
        );

        // Check if the student slot intersects with teacher availability
        // Handle the case where session crosses midnight
        let isWithinShift = false;

        if (istEndHour > istStartHour) {
          // Session doesn't cross midnight in IST
          isWithinShift = isSlotWithinTeacherShift(
            istStartHour,
            istEndHour,
            teacherShift,
          );
        } else {
          // Session crosses midnight in IST - split into two parts
          const firstPartEnd = 24;
          const secondPartStart = 0;

          const firstPartValid = isSlotWithinTeacherShift(
            istStartHour,
            firstPartEnd,
            teacherShift,
          );
          const secondPartValid = isSlotWithinTeacherShift(
            secondPartStart,
            istEndHour,
            teacherShift,
          );

          // For night shifts, both parts need to be valid
          // For day shifts, this scenario shouldn't occur but we check anyway
          isWithinShift = firstPartValid && secondPartValid;
        }

        if (isWithinShift) {
          // console.log({ hour });
          addSlot(hour, endHour, istStartHour, istEndHour, timezone, tzAbbr);
        }
      }
    });

    // Create continuous slots before adding to results
    const continuousSlots = createContinuousSlots(daySlots);

    // console.log('asdasdasdasd', continuousSlots);

    // Add day to results if slots exist
    if (continuousSlots.length > 0) {
      results.push({
        key: dayKey,
        label: dayLabel,
        subLabel,
        dayType,
        timeSlots: {
          type: 'time',
          subTitle: 'Choose a time slot',
          slots: continuousSlots,
        },
      });
    }
  }

  return results;
}

export const EXPERIMENT_KEYS = {
  ECNA: 'ecnaV4',
  DOWNLOAD_JOURNEY: 'downloadJourney',
  ECNA_EXPERIMENTS_WITH_NO_INTRO: 'ecnaNoIntro',
  ECNA_ADVANCED_COURSE: 'ecnaAdvancedCourse',
  SHOW_CHAT_BOT: 'showChatBot',
  ENABLE_CLOUDFLARE_CACHING: 'enableCloudflareCaching',
  WEBSITE_REVAMP: 'websiteRevamp',
  CONTENT_PAGE_BANNER: 'contentPageBanner',
  SUBJECT_FOLD: 'subjectFold',
  HOME_PAGE_CONTENT: 'homePageContent',
};

export const VARIANTS = {
  CONTROL_VARIANT: 'a',
  VARIANT_B: 'b',
  VARIANT_C: 'c',
  VARIANT_D: 'd',
  VARIANT_E: 'e',
  VARIANT_F: 'f',
  VARIANT_G: 'g',
  VARIANT_H: 'h',
  VARIANT_I: 'i',
  VARIANT_J: 'j',
  VARIANT_K: 'k',
  VARIANT_L: 'l',
  VARIANT_M: 'm',
  VARIANT_N: 'n',
  VARIANT_P: 'p',
  VARIANT_Q: 'q',
  VARIANT_R: 'r',
  VARIANT_S: 's',
  VARIANT_T: 't',
  VARIANT_W: 'w',
  VARIANT_X: 'x',
  VARIANT_V: 'v',
  VARIANT_U: 'u',
  VARIANT_Y: 'y',
  VARIANT_Z: 'z',
  VARIANT_AA: 'aa',
  VARIANT_AB: 'ab',
  VARIANT_AC: 'ac',
  VARIANT_AD: 'ad',
  VARIANT_AE: 'ae',
  VARIANT_AF: 'af',
  VARIANT_AG: 'ag',
  VARIANT_AH: 'ah',
  VARIANT_AI: 'ai',
  VARIANT_AJ: 'aj',
  VARIANT_AK: 'ak', // ROW Direct Admissions
  VARIANT_AL: 'al',
  VARIANT_AM: 'am', // US Direct Admissions With Info Text
  VARIANT_AN: 'an', // US Direct Admissions WithOut Info Text
  VARIANT_AO: 'ao', // India DA With Info Text
  VARIANT_AP: 'ap', // India DA Without Info Text
};

export const slotsType = {
  singleSlots: 'singleSlots',
  doubleSlots: 'doubleSlots',
  sevenDaySlots: 'sevenDaySlots',
};

export const VARIANTS_SLOT_TYPE = {
  [VARIANTS.VARIANT_AI]: 'pickSlotSevenDayConfig',
  [VARIANTS.VARIANT_AJ]: 'pickSlotSevenDayConfig',
  [VARIANTS.VARIANT_AK]: 'pickSlotSevenDayConfig',
  [VARIANTS.VARIANT_AL]: 'pickSlotSevenDayConfig',
  [VARIANTS.VARIANT_AM]: 'pickSlotSevenDayConfig',
  [VARIANTS.VARIANT_AD]: 'pickSlotSevenDayConfig',
  [VARIANTS.VARIANT_AN]: 'pickSlotSevenDayConfig',
  [VARIANTS.VARIANT_AO]: 'pickSlotSevenDayConfig',
  [VARIANTS.VARIANT_AP]: 'pickSlotSevenDayConfig',
};

export const getSlotPickType = (ecnaExptVariant, slotPickType) => {
  if (slotPickType) return slotPickType;
  if (ecnaExptVariant && VARIANTS_SLOT_TYPE[ecnaExptVariant]) {
    return VARIANTS_SLOT_TYPE[ecnaExptVariant];
  }

  return '';
};

export const getSlotsType = (ecnaExperiments, configSlotPickType) => {
  if (configSlotPickType === 'pickSlotSevenDayConfig') {
    return slotsType.sevenDaySlots;
  }
  if (
    !configSlotPickType &&
    [
      VARIANTS.VARIANT_AI,
      VARIANTS.VARIANT_AJ,
      VARIANTS.VARIANT_AK,
      VARIANTS.VARIANT_AL,
      VARIANTS.VARIANT_AM,
      VARIANTS.VARIANT_AD,
      VARIANTS.VARIANT_AN,
      VARIANTS.VARIANT_AO,
      VARIANTS.VARIANT_AP,
    ].includes(ecnaExperiments)
  ) {
    return slotsType.sevenDaySlots;
  }

  return slotsType.singleSlots;
};

export const slotDaysConfig = slotPickType => {
  const config = {
    defaultSlotDaysConfig: [
      [3, 4, 6, 7],
      [3, 4, 5, 6],
      [3, 4, 5, 6],
      [3, 4, 5, 6],
      [4, 5, 9, 10],
      [3, 4, 8, 9],
      [3, 4, 7, 8],
    ],
    pickSlotAfterOneDayConfig: [
      [2, 3, 6, 7],
      [2, 3, 5, 6],
      [2, 3, 4, 5],
      [2, 3, 4, 5],
      [2, 3, 4, 5],
      [2, 3, 4, 8],
      [2, 3, 7, 8],
    ],
    // To be removed
    pickSlotAfterFourDayConfig: [
      [4, 5, 8, 9],
      [4, 5, 7, 8],
      [4, 5, 6, 7],
      [4, 5, 6, 7],
      [4, 5, 6, 7],
      [4, 5, 6, 10],
      [4, 5, 9, 10],
    ],
    pickSlotSevenDayConfig: [
      [4, 5, 6, 7, 8, 9, 10],
      [4, 5, 6, 7, 8, 9, 10],
      [4, 5, 6, 7, 8, 9, 10],
      [4, 5, 6, 7, 8, 9, 10],
      [4, 5, 6, 7, 8, 9, 10],
      [4, 5, 6, 7, 8, 9, 10],
      [4, 5, 6, 7, 8, 9, 10],
    ],
    // To be removed
    pickSlotSevenDayConfigWithFourDayBuffer: [
      [4, 5, 6, 7, 8, 9, 10],
      [4, 5, 6, 7, 8, 9, 10],
      [4, 5, 6, 7, 8, 9, 10],
      [4, 5, 6, 7, 8, 9, 10],
      [4, 5, 6, 7, 8, 9, 10],
      [4, 5, 6, 7, 8, 9, 10],
      [4, 5, 6, 7, 8, 9, 10],
    ],
  };

  if (slotPickType && config[slotPickType]) return config[slotPickType];

  return config.defaultSlotDaysConfig;
};

export const weekendDayInDigits = [0, 6];

export const monthArray = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sept',
  'Oct',
  'Nov',
  'Dec',
];

export const DAY = {
  Sun: 'Sunday',
  Mon: 'Monday',
  Tue: 'Tuesday',
  Wed: 'Wednesday',
  Thu: 'Thursday',
  Fri: 'Friday',
  Sat: 'Saturday',
};

export const dayTypes = {
  weekday: 'weekday',
  weekend: 'weekend',
};

const getDateTimeStamps = (slotsDays, slotType) => {
  const defaultSlots = [];
  const alternateSlots = [];
  const data = { defaultSlots };
  const isDoubleSlots = slotType === slotsType.doubleSlots;

  slotsDays.forEach(noOfDaysFromToday => {
    const date = new Date();

    date.setDate(date.getDate() + noOfDaysFromToday);
    date.setHours(0, 0, 0, 0); // making time 12am
    const baseTime = date.getTime() / 1000;
    const day = date.getDay();
    const isWeekend = weekendDayInDigits.includes(day);
    const dateData = {
      key: baseTime,
      label: `${monthArray[date.getMonth()]} ${date.getDate()}`,
      subLabel: Object.keys(DAY)[date.getDay()],
      dayType: isWeekend ? dayTypes.weekend : dayTypes.weekday,
    };

    if (isDoubleSlots && isWeekend) {
      alternateSlots.push(dateData);
    } else {
      defaultSlots.push(dateData);
    }
  });

  if (isDoubleSlots) {
    data.alternateSlots = alternateSlots;
  }

  return data;
};

export const HOUR_FORMAT = {
  TWELVE_HOUR_FORMAT: 12,
  TWENTY_FOUR_HOUR_FORMAT: 24,
};

export const getMeridiemAccordingToHourAndHourFormat = (
  hour,
  hourFormat,
  meridiem = '',
) => {
  if (hourFormat === HOUR_FORMAT.TWENTY_FOUR_HOUR_FORMAT) {
    return hour / 12 >= 1 ? 'PM' : 'AM';
  }

  return hour === 12 ? 'PM' : meridiem;
};

export const getTime = (baseTime, requiredTime) => {
  return baseTime + requiredTime * 60 * 60; // converting to seconds
};

const getTwelveHoursTime = hour => {
  if (hour > 12) {
    return hour - 12;
  }

  return hour;
};

const getFromToLabel = (from, to) => {
  const fromMeridiem = getMeridiemAccordingToHourAndHourFormat(
    from,
    HOUR_FORMAT.TWENTY_FOUR_HOUR_FORMAT,
  );
  const toMeridiem = getMeridiemAccordingToHourAndHourFormat(
    to,
    HOUR_FORMAT.TWENTY_FOUR_HOUR_FORMAT,
  );

  return `${getTwelveHoursTime(from)} ${fromMeridiem} - ${getTwelveHoursTime(
    to,
  )} ${toMeridiem}`;
};

// returns timestamps of slots
const getTimeStamps = (dateTimeStamps, weekdaySlots, weekendSlots, slotType) => {
  const timeStamps = { ...dateTimeStamps };
  const isSevenDaySlotPick = slotsType.sevenDaySlots === slotType;

  Object.keys(dateTimeStamps).forEach(slotTypeKey => {
    timeStamps[slotTypeKey] = dateTimeStamps[slotTypeKey].map(({ key, dayType }) => {
      const slotsMap =
        dayType === dayTypes.weekday && !isSevenDaySlotPick ? weekdaySlots : weekendSlots;

      return slotsMap.map(({ from, to }) => {
        const fromTimeStamp = getTime(key, from);
        const toTimeStamp = getTime(key, to);

        return {
          key: fromTimeStamp,
          from: fromTimeStamp,
          to: toTimeStamp,
          label: getFromToLabel(from, to),
        };
      });
    });
  });

  return timeStamps;
};

// 24 hr format
export const weekdaySlotTimeMap = [
  { from: 14, to: 16 },
  { from: 16, to: 18 },
  { from: 18, to: 20 },
];

export const weekendSlotTimeMap = [
  { from: 10, to: 12 },
  { from: 12, to: 14 },
  { from: 14, to: 16 },
  { from: 16, to: 18 },
  { from: 18, to: 20 },
];

// returns all the dates along with respective time slots
const getDateTimeSlots = (
  dateTimeStamps,
  slotTimeStamps,
  defaultTimeData,
  isAlternateSlot,
) => {
  // console.log({ dateTimeStamps });
  // console.log({ slotTimeStamps });
  const dateConfig = dateTimeStamps.map((date, index) => ({
    ...date,
    timeSlots: {
      ...defaultTimeData,
      type: isAlternateSlot ? 'alternate_time' : 'time',
      slots: slotTimeStamps[index],
    },
  }));

  // console.log('kkkkk', { dateConfig });

  return dateConfig;
};

export const slotTimeDefaultData = {
  type: 'time',
  subTitle: 'Choose time',
  slotsStyle: {
    marginRight: '10px',
    height: '40px',
    flexWrap: 'wrap',
  },
};

export const slotTimeMobileData = {
  ...slotTimeDefaultData,
  slotsStyle: {
    ...slotTimeDefaultData.slotsStyle,
    minWidth: '109px',
    mobileMarginBottom: '12px',
    mobileJustifyContent: 'flex-start',
  },
};

export const sevenDayTimeMobileSlot = {
  ...slotTimeMobileData,
  subTitle: 'Choose A time slot',
  slotsStyle: {
    ...slotTimeDefaultData.slotsStyle,
    marginRight: '5px',
    mobileMarginBottom: '8px',
    minWidth: '109px',
    mobileWidth: '107px',
    mobileJustifyContent: 'flex-start',
  },
};

export const slotTimeDesktopData = {
  ...slotTimeDefaultData,
  slotsStyle: {
    ...slotTimeDefaultData.slotsStyle,
    minWidth: '124px',
    justifyContent: 'flex-start',
  },
};

export const sevenDayTimeDesktopSlot = {
  ...slotTimeDesktopData,
  subTitle: 'Choose A time slot',
  slotsStyle: {
    ...slotTimeDefaultData.slotsStyle,
    marginRight: '10px',
    minWidth: '123px',
    justifyContent: 'flex-start',
  },
};

const getDefaultTimeData = (isMobile, slotType) => {
  const isSevenDaySlotPick = slotType === slotsType.sevenDaySlots;

  if (isSevenDaySlotPick) {
    return isMobile ? sevenDayTimeMobileSlot : sevenDayTimeDesktopSlot;
  }

  return isMobile ? slotTimeMobileData : slotTimeDesktopData;
};

export const dateDefaultData = {
  type: 'date',
  title: 'Please select your preferred date & time',
  slotsStyle: {
    minWidth: '92px',
    height: '48px',
    textFlexDirection: 'column-reverse',
  },
};

export const mobileDateDefaultData = {
  ...dateDefaultData,
  slotsStyle: {
    ...dateDefaultData.slotsStyle,
    mobileWidth: '162px',
    marginRight: '8px',
    mobileJustifyContent: 'flex-start',
    mobileMarginBottom: '12px',
  },
};

export const sevenDaySlotMobileData = {
  ...mobileDateDefaultData,
  title: 'Select a date',
  slotsStyle: {
    ...dateDefaultData.slotsStyle,
    marginRight: '9px',
    mobileMarginBottom: '9px',
    minWidth: '76px',
    mobileWidth: '76px',
    height: '48px',
    mobileJustifyContent: 'flex-start',
    gap: '12px',
  },
};

export const desktopDateDefaultData = {
  ...dateDefaultData,
  slotsStyle: {
    ...dateDefaultData.slotsStyle,
    marginRight: '12px',
    justifyContent: 'flex-start',
  },
};

export const sevenDaySlotDesktopData = {
  ...desktopDateDefaultData,
  title: 'Select a date',
  slotsStyle: {
    ...dateDefaultData.slotsStyle,
    marginRight: '8px',
    minWidth: '73px',
    height: '48px',
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
  },
};

export const doubleSlotDateMobileData = {
  ...mobileDateDefaultData,
  title: 'Please select a time',
};

export const doubleSlotDateDesktopData = {
  ...desktopDateDefaultData,
  title: 'Please select a time',
};

export const singleSlotDateDesktopData = { ...desktopDateDefaultData };

export const singleSlotDateMobileData = { ...mobileDateDefaultData };

const getDefaultDateData = (isMobile, slotType) => {
  const isDoubleSlots = slotType === slotsType.doubleSlots;
  const isSevenDaySlotPick = slotType === slotsType.sevenDaySlots;

  if (isSevenDaySlotPick) {
    return isMobile ? sevenDaySlotMobileData : sevenDaySlotDesktopData;
  }

  if (isDoubleSlots) {
    return isMobile ? doubleSlotDateMobileData : doubleSlotDateDesktopData;
  }

  return isMobile ? singleSlotDateMobileData : singleSlotDateDesktopData;
};

const getDefaultData = (isMobile, slotType) => {
  const defaultTimeData = getDefaultTimeData(isMobile, slotType);
  const defaultDateData = getDefaultDateData(isMobile, slotType);

  return { defaultTimeData, defaultDateData };
};

export const defaultSlotsData = {
  type: slotsType.singleSlots,
  title:
    'To schedule a trial class with your teacher, please tell us when both you and your child are available',
  buttonProps: {
    label: 'Confirm',
    fontWeight: '500',
    width: '224px',
  },
};

export const doubleSlotsData = {
  ...defaultSlotsData,
  type: slotsType.doubleSlots,
  title:
    'To help us schedule a trial class with your teacher, please tell us when both You and child are available',
  subTitle:
    "Please pick two time slots, one over weekend and one over weekday. This will help us make sure you don't miss out on the best teacher.",
};
export const sevenDaySlotsData = {
  ...defaultSlotsData,
  type: slotsType.sevenDaySlots,
  title:
    'To help us schedule a trial class with your teacher, please tell us when both You and child are available',
  subTitle: 'Please make sure both you and your child are available',
};

export const slotsTextData = {
  [slotsType.singleSlots]: defaultSlotsData,
  [slotsType.doubleSlots]: doubleSlotsData,
  [slotsType.sevenDaySlots]: sevenDaySlotsData,
};

const getTitle = (ecnaExperiments, defaultTitle) => {
  const { isSeparateSchoolSlotsAfterSignup } = ecnaExperiments || {};
  let title = defaultTitle;

  if (isSeparateSchoolSlotsAfterSignup) {
    title =
      'Please pick a time to meet your teacher for 1-on-1 live class when both you and your child are available';
  }

  return title;
};

// returns final data in required format
const getConfig = (
  isMobile,
  dateTimeStamps,
  slotTimeStamps,
  slotType,
  ecnaExperiments,
  slotPickQuestionData,
) => {
  const isDoubleSlots = slotType === slotsType.doubleSlots;
  // console.log({ isDoubleSlots });
  const { subTitle } = slotPickQuestionData;
  const { defaultTimeData, defaultDateData } = getDefaultData(isMobile, slotType);
  // console.log('qqqqqq', Object.keys(dateTimeStamps), dateTimeStamps);
  const slotsData = Object.keys(dateTimeStamps).map(key => {
    const { dayType } = dateTimeStamps[key][0];
    const isWeekend = dayType === dayTypes.weekend;
    // console.log({ isWeekend });

    // console.log(
    //   'dddddddd',
    //   getDateTimeSlots(
    //     dateTimeStamps[key],
    //     slotTimeStamps[key],
    //     defaultTimeData,
    //     isDoubleSlots && isWeekend,
    //   ),
    // );

    return {
      ...defaultDateData,
      title: subTitle || defaultDateData.title,
      dayType,
      type: isDoubleSlots && isWeekend ? 'alternate_date' : 'date',
      slots: getDateTimeSlots(
        dateTimeStamps[key],
        slotTimeStamps[key],
        defaultTimeData,
        isDoubleSlots && isWeekend,
      ),
    };
  });

  // console.log('sssss', { slotsData });

  const data = {
    ...slotsTextData[slotType],
    title: getTitle(ecnaExperiments, slotsTextData[slotType]?.title),
    slotsData,
  };

  return data;
};

export const getDemoSlots = (
  slotType,
  isMobile,
  ecnaExperiments,
  slotPickQuestionData,
  slotPickType,
) => {
  const day = new Date().getDay();
  const slotsDays = slotDaysConfig(slotPickType)[day];
  const slotsdates = getDateTimeStamps(slotsDays, slotType);
  const slotsTime = getTimeStamps(
    slotsdates,
    weekdaySlotTimeMap,
    weekendSlotTimeMap,
    slotType,
  );

  // console.log({ day, slotsDays, slotsdates, slotsTime });

  const config = getConfig(
    isMobile,
    slotsdates,
    slotsTime,
    slotType,
    ecnaExperiments,
    slotPickQuestionData,
  );

  // console.log({ config });

  return config;
};

export const getFromToTime = (fromTime, slotType) => {
  if (Object.values(slotsType).includes(slotType)) {
    return { from: fromTime, to: getTime(fromTime, 1) }; // 1hr diff for slots
  }

  return fromTime;
};

export const QUESTION_DATA = {
  slot_pick: {
    id: 'slot-pick',
    title: 'Please select your preferred date & time',
    subTitle: '',
    slotPickType: 'pickSlotSevenDayConfig',
    elnableTimezoneSelect: true,
  },
};

export function capitalizeFirstLetter(string) {
  if (!string) {
    return string;
  }

  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
}

export const usTimezones = [
  {
    id: 'America/Los_Angeles',
    value: 'America/Los_Angeles',
    label: 'Pacific Time (GMT - 7:00)',
  },
  {
    id: 'America/Denver',
    value: 'America/Denver',
    label: 'Mountain Time (GMT - 6:00)',
  },
  {
    id: 'America/Chicago',
    value: 'America/Chicago',
    label: 'Central Time (GMT - 5:00)',
  },
  {
    id: 'America/New_York',
    value: 'America/New_York',
    label: 'Eastern Time (GMT - 4:00)',
  },
];

export const determineMatchingTimezoneUS = currentZone => {
  // eslint-disable-next-line consistent-return
  const timezoneArray = usTimezones.filter(timezone => {
    return timezone.value === currentZone;
  });

  if (timezoneArray.length > 0) {
    return timezoneArray[0].label;
  }

  return currentZone;
};

/**
 * Transforms regional trial slots API data into the format expected by the slot UI
 * @param {Object} data - API response with format: { "DD-MM-YYYY": { "HH-HH": 1|0 } }
 * @param {string} userTimezone - The user's selected timezone (e.g., "America/New_York")
 * @returns {Array} Array of date objects with nested time slots in the expected UI format
 */
export function transformRegionalSlotsData(data, userTimezone) {
  if (!data || typeof data !== 'object' || Object.keys(data).length === 0) {
    return [];
  }

  const results = [];

  const sortedDates = Object.keys(data)
    .filter(key => {
      // Only process keys that match the date format DD-MM-YYYY
      // This filters out keys like "available_slots" or other metadata
      return /^\d{1,2}-\d{1,2}-\d{4}$/.test(key);
    })
    .sort((a, b) => {
      const [dayA, monthA, yearA] = a.split('-').map(Number);
      const [dayB, monthB, yearB] = b.split('-').map(Number);
      const dateA = new Date(yearA, monthA - 1, dayA);
      const dateB = new Date(yearB, monthB - 1, dayB);
      return dateA - dateB;
    });

  sortedDates.forEach(dateStr => {
    const timeSlots = data[dateStr];

    // Parse date string (DD-MM-YYYY format)
    const dateParts = dateStr.split('-');
    const day = parseInt(dateParts[0], 10);
    const month = parseInt(dateParts[1], 10);
    const year = parseInt(dateParts[2], 10);

    // Validate parsed values
    if (isNaN(day) || isNaN(month) || isNaN(year)) {
      console.warn(`Invalid date format: ${dateStr}`);
      return;
    }

    // Create DateTime in the user's timezone
    const dateTime = DateTime.fromObject(
      {
        year,
        month,
        day,
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0,
      },
      { zone: userTimezone || 'UTC' },
    );

    if (!dateTime.isValid) {
      console.warn(`Invalid date: ${dateStr}, reason: ${dateTime.invalidReason}`);
      return;
    }

    const dayOfWeek = dateTime.weekday % 7; // Luxon: 1=Monday...7=Sunday, convert to JS: 0=Sunday...6=Saturday
    const dayKey = Math.floor(dateTime.toSeconds());
    const dayLabel = dateTime.toLocaleString({
      month: 'short',
      day: 'numeric',
    });
    const subLabel = dateTime.toLocaleString({ weekday: 'long' });
    const dayType = getDayType(dayOfWeek);

    // Helper function to determine time of day based on hour
    const getTimeOfDay = hour => {
      if (hour >= 0 && hour < 12) {
        return 'morning';
      }
      if (hour >= 12 && hour < 17) {
        return 'afternoon';
      }
      if (hour >= 17 && hour < 24) {
        return 'evening';
      }

      return 'morning'; // Default to morning for edge cases
    };

    // Process time slots for this date
    const availableTimeSlots = [];

    Object.keys(timeSlots).forEach(timeRange => {
      const availability = timeSlots[timeRange];

      // eval grid is a specialist count, trial grid is 0|1 — any positive = available
      if (!availability || availability < 1) {
        return;
      }

      // eval keys carry :30 ("HH:MM-HH:MM"); trial keys are hourly ("HH-HH", minute 0)
      const [startPart, endPart] = timeRange.split('-');
      const [startHour, startMinute = 0] = startPart.split(':').map(Number);
      const [endHour, endMinute = 0] = endPart.split(':').map(Number);

      // Validate parsed time values
      if (
        isNaN(startHour) ||
        isNaN(endHour) ||
        startHour < 0 ||
        startHour > 23 ||
        startMinute < 0 ||
        startMinute > 59 ||
        endHour < 0 ||
        endHour > 24
      ) {
        console.warn(`Invalid time range: ${timeRange} for date ${dateStr}`);
        return;
      }

      // Create slot datetime
      const slotDateTime = dateTime.set({
        hour: startHour,
        minute: startMinute,
        second: 0,
        millisecond: 0,
      });
      const slotEndDateTime = dateTime.set({
        hour: endHour,
        minute: endMinute,
        second: 0,
        millisecond: 0,
      });

      if (!slotDateTime.isValid) {
        console.warn(
          `Invalid slot datetime for ${dateStr} at ${timeRange}: ${slotDateTime.invalidReason}`,
        );
        return;
      }

      const fromEpoch = Math.floor(slotDateTime.toSeconds());
      const toEpoch = Math.floor(slotEndDateTime.toSeconds());

      // Get the time in user's selected timezone
      const userTimezoneDateTime = slotDateTime.setZone(userTimezone);
      const userEndTimezoneDateTime = slotEndDateTime.setZone(userTimezone);
      const userStartHour = userTimezoneDateTime.hour + userTimezoneDateTime.minute / 60;
      const userEndHour =
        userEndTimezoneDateTime.hour + userEndTimezoneDateTime.minute / 60;

      // Get timezone abbreviation for user's timezone using initials
      const tzAbbr = getInitials(userTimezoneDateTime.offsetNameLong);

      // Format the main label (user's selected timezone)
      const timeLabel = formatTimeLabel(userStartHour, userEndHour);

      // Convert to IST for the sublabel
      const istDateTime = slotDateTime.setZone('Asia/Kolkata');
      const istEndDateTime = slotEndDateTime.setZone('Asia/Kolkata');
      const istStartHour = istDateTime.hour + istDateTime.minute / 60;
      const istEndHour = istEndDateTime.hour + istEndDateTime.minute / 60;

      // Format IST time as sublabel
      const istLabel = `${formatTimeLabel(istStartHour, istEndHour)} IST`;

      availableTimeSlots.push({
        key: fromEpoch,
        from: fromEpoch,
        to: toEpoch,
        label: `${timeLabel} ${tzAbbr}`,
        subLabel: istLabel,
        timeOfDay: getTimeOfDay(userStartHour),
        metadata: {
          timezone: userTimezone || 'UTC',
          abbreviation: tzAbbr,
          istTime: `${istDateTime.hour}:${istDateTime.minute
            .toString()
            .padStart(2, '0')}-${
            istEndDateTime.hour
          }:${istEndDateTime.minute.toString().padStart(2, '0')}`,
        },
      });
    });

    // Sort time slots by start time
    availableTimeSlots.sort((a, b) => a.from - b.from);

    // Only add this date if it has available time slots
    if (availableTimeSlots.length > 0) {
      results.push({
        key: dayKey,
        label: dayLabel,
        subLabel,
        dayType,
        timeSlots: {
          type: 'time',
          subTitle: 'Choose a time slot',
          slots: availableTimeSlots,
        },
      });
    }
  });

  // Return in the format expected by SlotContainer
  return {
    title: 'Select a date',
    type: 'date',
    slots: results,
    slotsStyle: {
      marginRight: '8px',
      minWidth: '73px',
      height: '48px',
      justifyContent: 'flex-start',
      flexWrap: 'wrap',
    },
  };
}
